import React, { useState, useEffect } from 'react';

const CustomNotification = ({ notification, onClose, onNavigate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (notification) {
      // Show notification with animation
      setIsVisible(true);
      setProgress(100);

      // Progress bar animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev <= 0) {
            clearInterval(progressInterval);
            handleClose();
            return 0;
          }
          return prev - 2; // Decrease by 2% every 100ms (5 second total)
        });
      }, 100);

      // Auto-hide after 5 seconds
      const timeout = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => {
        clearInterval(progressInterval);
        clearTimeout(timeout);
      };
    }
  }, [notification]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  const handleClick = () => {
    if (onNavigate) {
      onNavigate();
    }
    handleClose();
  };

  if (!notification) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 10000,
      transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease-in-out',
      maxWidth: '350px',
      width: '100%'
    }}>
      <div 
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          borderRadius: '15px',
          padding: '16px',
          border: '2px solid #00E9FF',
          boxShadow: '0 10px 30px rgba(0, 233, 255, 0.3)',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            color: 'rgba(255, 255, 255, 0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
          }}
        >
          ✕
        </button>

        {/* Notification content */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Avatar */}
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #00E9FF, #E0005B)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            flexShrink: 0
          }}>
            {notification.avatar || '💕'}
          </div>

          {/* Message content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: '#00E9FF',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {notification.title || 'New Message'}
            </div>
            
            <div style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '13px',
              lineHeight: '1.3',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {notification.body || notification.message}
            </div>
            
            <div style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '11px',
              marginTop: '4px'
            }}>
              Just now • Tap to view
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
          width: `${progress}%`,
          transition: 'width 0.1s linear'
        }} />

        {/* Flirteen branding */}
        <div style={{
          position: 'absolute',
          bottom: '4px',
          right: '8px',
          fontSize: '9px',
          color: 'rgba(255, 255, 255, 0.4)',
          fontWeight: '600'
        }}>
          Flirteen
        </div>
      </div>
    </div>
  );
};

// Hook for managing notifications
export const useCustomNotifications = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (notificationData) => {
    setNotification(notificationData);
  };

  const closeNotification = () => {
    setNotification(null);
  };

  return {
    notification,
    showNotification,
    closeNotification
  };
};

export default CustomNotification;